import React, { useState } from 'react';
import { differenceInCalendarDays } from 'date-fns';
import * as Sentry from '@sentry/nextjs';

import Dialog from 'epn-ui/umd/dist/components/Dialog';
import apiHooks from '@apiHooks';

import { IGetMotivationData } from '@api/types';

import useI18n from '@i18n';
import Header from './Header';
import LineWrapper from './LineWrapper';

import {
    getProgressLine,
    getProportionsOfLines,
    prepareGoalsMap,
    prepareMotivationData,
} from './utils';

import { ILineData } from './interfaces';

const { useMotivations } = apiHooks;

const BaseMotivation: React.FC = () => {
    const { t } = useI18n('Statistic');
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const { data, error, isLoading } = useMotivations<IGetMotivationData>();

    if (error) {
        console.error(error);
        Sentry.captureException(error);
    }

    if (isLoading || !data || !data?.isParticipant || data?.goals.length < 2 || !!error) {
        return null;
    }

    const preparedData = prepareMotivationData(data);
    // Максимальное число цели
    const calcWidth = data.goals[data.goals.length - 1].goalGmv;
    const goalsMap = prepareGoalsMap(data.goals);

    const lineData: ILineData = {
        goals: goalsMap,
        proportions: getProportionsOfLines(goalsMap, calcWidth),
        progress: getProgressLine(goalsMap, data.currentGmv),
        currentGmv: data.currentGmv,
    };

    const endDate = differenceInCalendarDays(new Date(data.goals[1].endAt), Date.now());

    const handleModal = () => {
        setIsModalOpen(!isModalOpen);
    };

    return (
        <div className="p-6 rounded-2 shadow-2">
            <Header
                endDate={endDate}
                handleModal={handleModal}
                onlyAffGmvGoals={data?.onlyAffGmvGoals}
            />
            <LineWrapper
                preparedData={preparedData}
                lineData={lineData}
                endDate={endDate}
                handleModal={handleModal}
            />
            <Dialog
                open={isModalOpen}
                title={t('Motivation program levels')}
                onCancel={handleModal}
            >
                <div className="mb-4">
                    {data.levels.map(level => (
                        <div className="py-1.5" key={level.number}>
                            <h5 className="pb-1 font-bold capitalize">
                                {t('level n', { level: level.number })}
                            </h5>
                            <p>
                                {t('Turnover')}{' '}
                                {level.gmvLowerBound === 0 && level.gmvUpperBound !== 0 && (
                                    <span className="font-bold">
                                        {t('turnover to', { to: level.gmvUpperBound })}
                                    </span>
                                )}{' '}
                                {level.gmvLowerBound !== 0 && level.gmvUpperBound === 0 && (
                                    <span className="font-bold">
                                        {t('turnover from', { from: level.gmvLowerBound })}
                                    </span>
                                )}{' '}
                                {level.gmvLowerBound !== 0 && level.gmvUpperBound !== 0 && (
                                    <span className="font-bold">
                                        {t('turnover between border', {
                                            from: level.gmvLowerBound,
                                            to: level.gmvUpperBound,
                                        })}
                                    </span>
                                )}{' '}
                                <span className="font-bold">
                                    {t('levelModal over the last three months')}
                                </span>{' '}
                                {t('(excluding the current one)')}{' '}
                                {t('The rate for non-affiliate products is')}{' '}
                                <span className="font-bold">
                                    {level.number < 3 ? '0.008' : '0.2'}%
                                </span>
                                .
                            </p>
                        </div>
                    ))}
                </div>
            </Dialog>
        </div>
    );
};

export default BaseMotivation;
